import * as _ from "lodash";
import { AnyAction } from "redux";
import { Dispatch } from "redux";
import { ActionCreator, bindActionCreators } from "redux";
import { BatchAction, batchActions } from "redux-batched-actions";
import { ThunkAction } from "redux-thunk";
import { IDragBoxProperties } from "../../widgets/components/DragSelectionBox";
import { IAnyObjectType } from "../../widgets/components/SelectionBox";
import { CanvasMode, ShapeType } from "../../widgets/interfaces/model/enumerations/index";
import { IPageObjectTypes } from "../../widgets/interfaces/model/enumerations/PageObjectTypes";
import { ICanvasEditorState, IPage } from "../../widgets/interfaces/model/index";
import { IGroupShape } from "../../widgets/interfaces/model/PageObjects/GroupShape";
import { IShapeProperties } from "../../widgets/interfaces/model/properties/index";
import { getSelectedPage } from "../../widgets/selectors/page";
import {
	CanvasEditorConstants,
	IAdjustGroupExtentsAction,
	IAdjustHighlightAction,
	IApplyTextFormattingAction,
	IClearSubgroupAction,
	IDeselectAllObjectAction,
	IDeselectObjectAction,
	ISelectObjectAction,
	ISelectObjectsByIndexAction,
	ISetAllAspectRatiosAction,
	ISetAllTextDefaultStylesAction,
	ISetAspectRatioAction,
	ISetCanvasModeAction,
	ISetFirstCharStyleAction,
	ISetImpossiblePathsAction,
	ISetShapeDefaultPropertiesAction,
	ISetShapeDrawMetadataAction,
	ISetShapeDrawToolAction,
	ISetTextEditorInstanceAction,
	ISetTextStylesDefaultAction,
	PageActionConstants,
} from "../reducers/index";
import { SelectionConstants } from "../reducers/Selection";
import { getShapeDrawMetadata, pageObjectsInBox } from "../reducers/util";

/**
 * Select page action creator.
 * @param selectedPage {Number} index of page to be selected. (1 base)
 * @returns {BatchAction} Select page action.
 */
export const selectPage: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = (selectedPage: number) =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const actions: AnyAction[] = [];
		const { mode } = state.canvasEditor;
		// on page change if mode is EDIT_TEXT or EDIT_PICTURE switching mode to Select Mode
		if (mode === CanvasMode.DRAG_TYPE_INSTANCE ||
			mode === CanvasMode.EDIT_PICTURE ||
			mode === CanvasMode.EDIT_TEXT) {
			actions.push({
				mode: CanvasMode.SELECT,
				transient: true,
				type: CanvasEditorConstants.SET_CANVAS_MODE,
			} as ISetCanvasModeAction);
		}
		actions.push({
			transient: true,
			type: SelectionConstants.SELECT_PAGE,
			selectedPage,
		});
		return dispatch(batchActions(actions));
	};

/**
 * Select page action creator.
 * @param selectedPage {string} id of page to be selected.
 * @returns {BatchAction} Select page action.
 */
export const selectPageById: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> = (pageId: string) =>
	(dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const actions: AnyAction[] = [];
		const { mode } = state.canvasEditor;
		// on page change if mode is EDIT_TEXT or EDIT_PICTURE switching mode to Select Mode
		if (mode === CanvasMode.DRAG_TYPE_INSTANCE ||
			mode === CanvasMode.EDIT_PICTURE ||
			mode === CanvasMode.EDIT_TEXT) {
			actions.push({
				mode: CanvasMode.SELECT,
				transient: true,
				type: CanvasEditorConstants.SET_CANVAS_MODE,
			} as ISetCanvasModeAction);
		}

		// find index of the page.
		let selectedPage = _.findIndex(state.pages.present, (page) => {
			return page.id === pageId;
		});
		selectedPage += 1; // selection uses base 1 for index.
		actions.push({
			transient: true,
			type: SelectionConstants.SELECT_PAGE,
			selectedPage,
		});
		return dispatch(batchActions(actions));
	};

/**
 * Select object action creator. Select new object only if mode is SELECT.
 * @param selectedObject {Number} index of object to be selected.
 * @param isMultiSelect {Boolean} if true, multiple objects to be selected
 * @returns {BatchAction} Select object action.
 */
export const selectObject: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(selectedObject: number, isMultiSelect: boolean, transient?: boolean, childKey?: number) =>
		(dispatch: Dispatch<BatchAction>, getState: () =>
			ICanvasEditorState): BatchAction => {
			// Batch action array
			const actions = [];
			const state: ICanvasEditorState = getState();
			const currentPage = getSelectedPage(state) as IPage;
			const currentSelection = state.selection.present.selectedPageObjects;
			const canvasEditor = state.canvasEditor;
			const pageObject = currentPage.pageObjects[selectedObject];
			const aspectRatio = pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;
			if (currentSelection.length !== 1) {
				childKey = -1;
			}

			const selectAction: ISelectObjectAction = {
				childKey,
				transient: true,
				type: SelectionConstants.SELECT_OBJECT,
				selectedObject,
				isMultiSelect,
			};

			const setAspectRatioAction: ISetAspectRatioAction = {
				aspectRatio,
				isMultiSelect,
				shapeId: pageObject.shapeId,
				transient: true,
				type: SelectionConstants.SET_ASPECT_RATIO,
			};

			const adjustGroupExtentsAction: IAdjustGroupExtentsAction = {
				selectedPage: state.selection.present.selectedPage,
				transient: true,
				type: PageActionConstants.ADJUST_GROUP_EXTENTS,
			};

			const { mode } = canvasEditor;

			if (
				(
					mode === CanvasMode.DRAG_TYPE_INSTANCE ||
					mode === CanvasMode.EDIT_CURVE ||
					mode === CanvasMode.EDIT_PICTURE ||
					mode === CanvasMode.EDIT_TEXT
				)
				// If we are not in select mode and we select a new shape, leave edit mode.  Otherwise,
				// if we stay editing the same shape, stay in edit mode.
				&& ((selectedObject !== currentSelection[0]) ||
					// If childkey is defined, that means we are selecting a subgroup element, and we need to
					// compare it against the current groupSelectionIndices to see if we are selecting a new
					// subgroup element or clicking the same one.
					(childKey !== undefined && state.selection.present.groupSelectionIndices !== undefined
						&& _.indexOf(state.selection.present.groupSelectionIndices, childKey) === -1))) {
				// If canvas was in EDIT_TEXT mode and other object is selected
				// then change mode and select other object.
				const modeChangeAction: ISetCanvasModeAction = {
					mode: CanvasMode.SELECT,
					transient: true,
					type: CanvasEditorConstants.SET_CANVAS_MODE,
				};
				actions.push(modeChangeAction);
				actions.push(selectAction);
				actions.push(setAspectRatioAction);
				actions.push(adjustGroupExtentsAction);
			} else if (canvasEditor.mode === CanvasMode.SELECT) {
				actions.push(selectAction);
				actions.push(setAspectRatioAction);
				actions.push(adjustGroupExtentsAction);
			}
			return dispatch(batchActions(actions));
		};

/**
 * Set the attribute keys that cannot be filled from this canvas type
 * @param paths {String[]} attribute keys that cannot be filled
 * @returns {ISetImpossiblePathsAction} Adjust highlight action.
 */
export const setImpossiblePaths: ActionCreator<ISetImpossiblePathsAction> =
	(paths: string[]) => {
		const setImpossiblePathsAction: ISetImpossiblePathsAction = {
			impossiblePaths: paths,
			type: SelectionConstants.IMPOSSIBLE_PATHS,
		};

		return setImpossiblePathsAction;
	};

/**
 * Adjust highlight action creator.
 */
export const adjustHighlight: ActionCreator<IAdjustHighlightAction> =
	(highlightType: string) => {
		const highlightAction: IAdjustHighlightAction = {
			highlightType,
			type: SelectionConstants.ADJUST_HIGHLIGHT,
		};

		return highlightAction;
	};

/**
 * Remove highlights action creator.  Resets all highlights to none.
 * @returns {IAdjustHighlightAction} Adjust highlight action.
 */
export const resetHighlight: ActionCreator<IAdjustHighlightAction> = () => {
	const highlightAction: IAdjustHighlightAction = {
		highlightType: undefined,
		type: SelectionConstants.ADJUST_HIGHLIGHT,
	};

	return highlightAction;
};

/**
 * Select object action creator. Select new object only if mode is SELECT.
 * @param selecionId {String} id of object to be selected.
 * @param isMultiSelect {Boolean} if true, multiple objects to be selected
 * @returns {BatchAction} Select object action.
 */
export const selectObjectById: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(selectionId: string, isMultiSelect: boolean, trans: boolean, forceSingleSelect?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () =>
			ICanvasEditorState): BatchAction => {
			function shapeMatchesId(shape: IAnyObjectType, id: string) {
				if (shape.shapeId === id) {
					return true;
				} else if (shape.type === IPageObjectTypes.GROUP_SHAPE) {
					let result = false;
					_.each((shape as IGroupShape).childObjects, (s) => {
						result = result || shapeMatchesId(s, id);
					});
					return result;
				} else {
					return false;
				}
			}

			// Batch action array
			const actions = [];
			const state: ICanvasEditorState = getState();
			const currentPage = getSelectedPage(state) as IPage;
			const currentSelection = state.selection.present.selectedPageObjects;
			const canvasEditor = state.canvasEditor;
			const selectedObject = _.findIndex(currentPage.pageObjects, (o) => {
				return shapeMatchesId(o, selectionId);
			});

			const groupObject = currentPage.pageObjects[selectedObject] as IGroupShape;
			let childKey = -1;
			if (groupObject && groupObject.childObjects) {
				childKey = _.findIndex(groupObject.childObjects, (o) => {
					return shapeMatchesId(o, selectionId);
				});
			}

			if (selectedObject > -1) {
				const pageObject = currentPage.pageObjects[selectedObject];
				const aspectRatio = pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;

				const selectAction: ISelectObjectAction = {
					childKey,
					forceSingleSelect,
					transient: trans,
					type: SelectionConstants.SELECT_OBJECT,
					selectedObject,
					isMultiSelect,
				};

				const setAspectRatioAction: ISetAspectRatioAction = {
					aspectRatio,
					isMultiSelect,
					shapeId: pageObject.shapeId,
					transient: true,
					type: SelectionConstants.SET_ASPECT_RATIO,
				};

				const adjustGroupExtentsAction: IAdjustGroupExtentsAction = {
					selectedPage: state.selection.present.selectedPage,
					transient: true,
					type: PageActionConstants.ADJUST_GROUP_EXTENTS,
				};

				const { mode } = canvasEditor;

				if (
					(
						mode === CanvasMode.DRAG_TYPE_INSTANCE ||
						mode === CanvasMode.EDIT_TEXT ||
						mode === CanvasMode.EDIT_PICTURE ||
						mode === CanvasMode.EDIT_CURVE
					) && selectedObject !== currentSelection[0]) {
					// If canvas was in EDIT_TEXT mode and other object is selected
					// then change mode and select other object.
					const modeChangeAction: ISetCanvasModeAction = {
						mode: CanvasMode.SELECT,
						transient: true,
						type: CanvasEditorConstants.SET_CANVAS_MODE,
					};
					actions.push(modeChangeAction);
					actions.push(selectAction);
					actions.push(setAspectRatioAction);
					actions.push(adjustGroupExtentsAction);
				} else if (canvasEditor.mode === CanvasMode.SELECT) {
					actions.push(selectAction);
					actions.push(setAspectRatioAction);
					actions.push(adjustGroupExtentsAction);
				}
			}

			return dispatch(batchActions(actions));
		};

/**
 * Deselect object action creator.
 * @param deselectedObject {Number} index of object to be deselected.
 * @returns {ISelectObjectAction} Select object action.
 */
export const deselectObject: ActionCreator<IDeselectObjectAction> =
	(deselectedObject: number): IDeselectObjectAction => ({
		transient: true,
		type: SelectionConstants.DESELECT_OBJECT,
		deselectedObject,
	});

/**
 * Deselect all objects action creator.
 * @returns {BatchAction} Deselect objects action
 */
export const deselectAllObjects: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(textCancel?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () =>
			ICanvasEditorState): BatchAction => {
			// Batch action array
			const actions: AnyAction[] = [];
			const state: ICanvasEditorState = getState();

			if (state.canvasEditor.mode === CanvasMode.EDIT_PEN) {
				const setCanvasModeAction: ISetCanvasModeAction = {
					mode: CanvasMode.SELECT,
					type: CanvasEditorConstants.SET_CANVAS_MODE,
				};
				actions.push(setCanvasModeAction);
			}

			// deselect all objects before selecting new objects
			const deselectAllObjectsAction: IDeselectAllObjectAction = {
				transient: true,
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};
			actions.push(deselectAllObjectsAction);

			const adjustGroupExtentsAction: IAdjustGroupExtentsAction = {
				selectedPage: state.selection.present.selectedPage,
				transient: true,
				type: PageActionConstants.ADJUST_GROUP_EXTENTS,
			};
			actions.push(adjustGroupExtentsAction);
			return dispatch(batchActions(actions));
		};

/**
 * Exit subgroup selection action creator.
 * @returns {BatchAction} Deselect objects action
 */
export const clearSubgroupSelection: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(textCancel?: boolean) =>
		(dispatch: Dispatch<BatchAction>, getState: () =>
			ICanvasEditorState): BatchAction => {
			// Batch action array
			const actions: AnyAction[] = [];
			const state: ICanvasEditorState = getState();

			// deselect all objects before selecting new objects
			const clearSubgroupAction: IClearSubgroupAction = {
				transient: true,
				type: SelectionConstants.CLEAR_SUBGROUP_SELECTION,
			};
			actions.push(clearSubgroupAction);

			const adjustGroupExtentsAction: IAdjustGroupExtentsAction = {
				selectedPage: state.selection.present.selectedPage,
				transient: true,
				type: PageActionConstants.ADJUST_GROUP_EXTENTS,
			};
			actions.push(adjustGroupExtentsAction);
			return dispatch(batchActions(actions));
		};

/**
 * Select all objects action creator
 * It creates ThunkAction which allows us to return a function instead of actual action object.
 * The returned function has two parameters.
 * 1. dispatch:  Function
 *      This is used dispatch our actual action object when we are done with thunk work (async or computational work)
 * 2. getState: Function
 *      This is used to get the state. This gives us chance to observe other state / sub-state tree and act accordingly
 *
 * Here, we get the selected page and its page objects from state.
 * Then we compute the selectedPageObjects array using selected page pageObject array, essentially selecting all objects
 */
export const selectObjectsByIndex: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	() => (dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		// Batch action array
		const actions = [];
		const state: ICanvasEditorState = getState();
		const page: IPage | null = getSelectedPage(state);
		let selectedPageObjects: number[] = [];
		if (page) {
			const aspectRatioMap: { [key: string]: number } = {};

			_.each(page.pageObjects, (pageObject) => {
				aspectRatioMap[pageObject.shapeId] = pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;
			});
			selectedPageObjects = page.pageObjects.map((value, index: number) => index);

			actions.push({
				transient: true,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
				selectedPageObjects,
			} as ISelectObjectsByIndexAction);

			const setAllAspectRatiosAction: ISetAllAspectRatiosAction = {
				aspectRatioMap,
				transient: true,
				type: SelectionConstants.SET_ALL_ASPECT_RATIOS,
			};
			actions.push(setAllAspectRatiosAction);

			const adjustGroupExtentsAction: IAdjustGroupExtentsAction = {
				selectedPage: state.selection.present.selectedPage,
				transient: true,
				type: PageActionConstants.ADJUST_GROUP_EXTENTS,
			};
			actions.push(adjustGroupExtentsAction);
		}
		return dispatch(batchActions(actions));
	};

/**
 * Set shape default properties action creator.
 * @param shapeProperties {IShapeProperties} Shape properties to set as defaults
 */
export const setDefaultShapeProperties: ActionCreator<ISetShapeDefaultPropertiesAction> =
	(shapeProperties: IShapeProperties) => {
		return ({
			transient: true,
			type: SelectionConstants.SET_SHAPE_DEFAULT_PROPERTIES,
			shapeProperties,
		});
	};

/**
 * Set text shape default properties action creator.
 * @param shapeProperties {IShapeProperties} Shape properties to set as defaults
 * @return Set text shape default action
 */
export const setTextShapeDefaultProperties: ActionCreator<ISetShapeDefaultPropertiesAction> =
	(shapeProperties: IShapeProperties) => {
		return ({
			transient: true,
			type: SelectionConstants.SET_TEXT_SHAPE_DEFAULT_PROPERTIES,
			shapeProperties,
		});
	};

/**
 * Set default text styles action creator.
 */
export const setTextDefaultStyles: ActionCreator<ISetTextStylesDefaultAction> =
	(style: string, value: string | undefined) => {
		return ({
			transient: true,
			type: SelectionConstants.SET_TEXT_DEFAULT_STYLES,
			style,
			value,
		});
	};

/**
 * Set selected draw tool.
 * @param shapeType {ShapeType | undefined} Shape type to be used. Custom shape will be used in case of undefined.
 * @param editOnComplete {boolean | undefined} Flag used to distinguish between text shape and normal shape
 * @param isTextShape { boolean | undefined} Used to decide whether to make shape editable or not on drag complete.
 * @param isTableShape { boolean | undefined} Used to know the shape is a table.
 */
export const setShapeDrawTool: ActionCreator<ISetShapeDrawToolAction> = (
	shapeType: ShapeType | undefined,
	editOnComplete: boolean | undefined,
	isTextShape: boolean | undefined,
	isTableShape: boolean | undefined,
	isPlaceholder: boolean | undefined,
) => {
	const shapeMetadata = getShapeDrawMetadata();
	// If isTableShape is NOT undefined, then take it's value.
	// If isTableShape IS undefined, then:
	//    if shapeMetadata is NOT undefined, then use true, else take isTableShape value
	const isTableShapeValue = (isTableShape === undefined) ?
		(shapeMetadata !== undefined ? true : isTableShape) : isTableShape;
	const action = {
		editOnComplete,
		isTableShape: isTableShapeValue,
		isPlaceholder,
		isTextShape,
		shapeType,
		transient: true,
		type: SelectionConstants.SET_SHAPE_TOOL,
	};
	return action;
};

/**
 * Set shape draw tool metadata.
 * @param metadata { any | undefined} Metadata for shape, for example if text table, rows and columns.
 */
export const setShapeDrawMetadata: ActionCreator<ISetShapeDrawMetadataAction> = (metadata: any | undefined) => {
	return {
		transient: true,
		type: SelectionConstants.SET_SHAPE_METADATA,
		metadata,
	};
};

/**
 * Set instance of the text editor created. This will be required for all text operations.
 * @param textEditorInstance Text editor instance to be used for operations.
 */
export const setTextEditorInstance: ActionCreator<ISetTextEditorInstanceAction> = (textEditorInstance: any) => ({
	transient: true,
	type: SelectionConstants.SET_TEXT_EDITOR_INSTANCE,
	textEditorInstance,
});

/**
 * Set source shape styles for format painter operation.
 */
export const setFormatPainterTextStyles: ActionCreator<IApplyTextFormattingAction> = (textStyles: any, valueStyles: any) => ({
	transient: true,
	type: SelectionConstants.SET_FORMAT_PAINTER_TEXT_STYLE,
	textStyles,
	valueStyles,
});

/**
 * Set shape first character styles action creator.
 * @param textStyles { any } styles
 */
export const setFirstCharStyle: ActionCreator<ISetFirstCharStyleAction> = (textStyles: any) => ({
	transient: true,
	type: SelectionConstants.SET_FIRST_CHAR_STYLE,
	textStyles,
});

/**
 * Select object by box action creator. Select object base on drag selection box.
 * @param selectBox {SVGRect} box of dragged select shape
 * @returns {BatchAction} Select object action.
 */
export const selectObjectByBox: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	(selectBox: IDragBoxProperties) =>
		(dispatch: Dispatch<BatchAction>, getState: () =>
			ICanvasEditorState): BatchAction => {
			// Batch action array
			const actions: AnyAction[] = [];
			const state: ICanvasEditorState = getState();
			const page: IPage = getSelectedPage(state) as IPage;
			const newSelection = pageObjectsInBox(page.pageObjects, selectBox);

			// deselect all objects before selecting new objects
			const deselectAllObjectsAction: IDeselectAllObjectAction = {
				transient: true,
				type: SelectionConstants.DESELECT_ALL_OBJECTS,
			};
			actions.push(deselectAllObjectsAction);

			const adjustGroupExtentsAction: IAdjustGroupExtentsAction = {
				selectedPage: state.selection.present.selectedPage,
				transient: true,
				type: PageActionConstants.ADJUST_GROUP_EXTENTS,
			};
			actions.push(adjustGroupExtentsAction);

			// selects new objects those are in drag selection box
			const selectAction: ISelectObjectsByIndexAction = {
				selectedPageObjects: newSelection,
				transient: true,
				type: SelectionConstants.STORE_OBJECT_SELECTIONS,
			};
			actions.push(selectAction);
			return dispatch(batchActions(actions));
		};

/**
 * Set default text styles object action creator.
 * @param textStyles {IAnyObjectType} defaults text styles
 */
export const setAllTextDefaultStyles: ActionCreator<ISetAllTextDefaultStylesAction> =
	(textStyles: IAnyObjectType) => {
		return ({
			transient: true,
			type: SelectionConstants.SET_ALL_TEXT_DEFAULTS_STYLES,
			textStyles,
		});
	};

/**
 * Set all aspect ratios action creator. Sets aspect ratio for all selected page objects.
 * @returns {BatchAction} Set all aspect ratios action.
 */
export const setAllAspectRatios: ActionCreator<ThunkAction<BatchAction, ICanvasEditorState, void, BatchAction>> =
	() => (dispatch: Dispatch<BatchAction>, getState: () => ICanvasEditorState): BatchAction => {
		const state: ICanvasEditorState = getState();
		const page: IPage = getSelectedPage(state) as IPage;
		const selectedPageObjects = state.selection.present.selectedPageObjects;
		const aspectRatioMap: { [key: string]: number } = {};

		_.each(selectedPageObjects, (selectedObject) => {
			const pageObject = page.pageObjects[selectedObject];
			aspectRatioMap[pageObject.shapeId] = pageObject.spPr.xfrm.ext.cx / pageObject.spPr.xfrm.ext.cy;
		});

		const setAllAspectRatiosAction: ISetAllAspectRatiosAction = {
			aspectRatioMap,
			transient: true,
			type: SelectionConstants.SET_ALL_ASPECT_RATIOS,
		};

		return dispatch(batchActions([setAllAspectRatiosAction]));
	};

/**
 * Selection APIs
 * Collection of redux dispatch bound actions
 */
export const initSelectionAPI = (dispatch: Dispatch<any>) => {
	return bindActionCreators({
		adjustHighlight,
		clearSubgroupSelection,
		deselectAllObjects,
		deselectObject,
		resetHighlight,
		selectObjectsByIndex,
		selectObject,
		selectObjectById,
		selectPage,
		selectPageById,
		setDefaultShapeProperties,
		setImpossiblePaths,
		setShapeDrawMetadata,
		setShapeDrawTool,
		setTextEditorInstance,
		setFirstCharStyle,
		setFormatPainterTextStyles,
		setTextDefaultStyles,
		selectObjectByBox,
		setTextShapeDefaultProperties,
		setAllTextDefaultStyles,
		setAllAspectRatios,
	}, dispatch);
};
